<template>
  <div>
    <input type="hidden" id="isPrint" value="false" />
    <input type="hidden" id="hidePrintTitle" value="false" />
    <input type="hidden" id="headerDate" value="false" />
    <input type="hidden" id="headerDateType" value="yearMonth" />

    <FieldTab v-if="tab.tabField" :addTabList="tabList" @moveTab="moveTab" />
    <Fragment v-else>
      <vs-tabs v-if="tabList.length > 0 && showOnlyOne" :value="currentTabIndex">
        <vs-tab v-for="(tab, tabIndex) in tabList" :key="`tab_${tab.label}_${tabIndex}`" :label="tab.label" @click="moveTab(tabIndex, tab)"></vs-tab>
      </vs-tabs>
      <vs-tabs v-if="subTabList.length > 0" style="margin-top: -14px" :value="subTabIndex">
        <vs-tab v-for="(tab, tabIndex) in subTabList" :key="`tab_${tab.label}_${tabIndex}`" :label="tab.label" @click="moveSubTab(tabIndex, tab)"></vs-tab>
      </vs-tabs>
    </Fragment>

    <!-- list -->
    <div v-if="tab && this.tabPageInfo.pageType === 'list'">
      <table-list-page
        :board-id="boardId"
        :board-slug="tab.slug"
        :use-finish-later="tab.useFinishLater"
        route-c="write|tab"
        route-u="write|update|tab"
        :permission="permission"
        :download="tabItems[boardId].title || tab.label"
        :create="useCreated && listUseCreateButton"
        :approval="tab.isApproval"
        :useRevision="tab.useRevision"
        :move-project="!(projectId > 0)"
        :by-current-location="tab.byCurrentLocation"
        :nav="nav"
        :heads="listTableHeads"
        :getCol="listGetTableColumn"
        :getRowStyle="listGetRowStyle"
        :getRowClass="listGetRowClass"
        :addRowClassStyles="listAddRowClassStyles"
        :prefixFunc="hasPrefixFunc ? this.tabPageInfo.component.prefixFunc : null"
        :query-search-mode="true"
        :multi-print="tab.usePrint && tab.multiPrint && (tab.isApproval || isMobile ? false : isAdmin)"
        :total-count-visible="tab.visibleTotalCount"
        :total-count-label="tab.totalCountLabel"
        :total-count-unit="tab.totalCountUnit"
        :useExcelDownloadList="tab.useExcelDownloadList"
        :useGhsLabelPrint="tab.useGhsLabelPrint"
        :useExcelDownloadPosts="tab.useExcelDownloadPosts"
        :listTheme="tab.listTheme"
        :minWidth="tab.minWidth"
        :filterSearch="tab.filterSearch"
        :client-paging="!!tab.clientPaging"
        :useSelectDelete="tab.useSelectDelete && isAdmin"
        @clickMultiPrint="listOnClickMultiPrint"
        @load="listOnLoadData"
        @clickSelectDownload="clickSelectDownload"
      >
        <template #search>
          <component :is="searchComponent" :value="nav.value" @input="listOnChangeSearch" />
        </template>
        <template #targets>
          <search-nav-item-tartgets v-if="nav.value && nav.value.companies" v-model="nav.value.companies" />
        </template>
        <template #toolRight>
          <vs-button v-if="listUseCreateButton && permission.c && tab.moveBoardWrite" color="secondary" @click="onClickMoveBoard" class="mb-4 flex-1 xl:flex-none">{{ tab.moveBoardWrite.label }}</vs-button>
        </template>
        <template v-if="listSummaryComponent" #totalCount>
          <component :is="listSummaryComponent" :board-id="boardId" :nav="nav" :project-id="projectId" :refresh-key="listReloadKey" />
        </template>
      </table-list-page>
    </div>

    <!-- write -->
    <div v-if="tab && this.tabPageInfo.pageType === 'write'">
      <approval-document2
        v-if="tab.isApproval"
        :tab="tab"
        :use-finish-later="tab.useFinishLater"
        :doc-info="writeDocInfo"
        :docProjectId="writeDocProjectId"
        :permission="permission"
        :usePrevPost="tab.usePrevPost"
        :parentId="tab.parentId"
        :useRevision="tab.useRevision"
        :prevPostButtonLabel="tab.prevPostButtonLabel"
        :use-print="tab.usePrint === true || false"
        :print-label="tab.printLabel"
        :use-delete="typeof tab.useDelete === 'boolean' ? tab.useDelete : true"
        :form="form.body"
        @board="setBoard"
        @move="moveTabPage"
        @delete="writeOnClickApprovalDelete"
        @close="writeOnClickApprovalClose"
        @recall="writeOnClickApprovalRecall"
        @upload="writeOnClickApprovalUpload"
        @update="writeOnClickApprovalSave"
        @save="writeOnClickApprovalSave"
        @revision="writeOnClickApprovalRevision"
        @approval="writeOnClickApprovalOk"
        @reject="writeOnClickApprovalNo"
        @prevPost="writeOnClickPrevPost"
        @retempsave="writeOnClickReTempsave"
        @copysave="writeOnClickCopysave"
      >
        <template #header="props">
          <div v-if="tab.useHeaderDate" class="h4 flex flex-wrap gap-4" style="align-items: center">
            <div id="print-label">
              <p class="font-bold h4 flex" style="align-items: center">{{ tabItems[boardId].title || tab.label }}</p>
            </div>

            <div class="flex items-center gap-4">
              <div v-for="type in writeHeaderDateTypes">
                <div v-if="type === 'date'" class="flex items-center">
                  <date-selector class="mr-2" style="width: 140px" v-model="form.header.date" :readonly="isReadonlyHeaderDate(props)" />
                </div>
                <div v-else-if="type === 'year'" class="flex items-center">
                  <vs-input
                    type="number"
                    class="ml-2"
                    style="width: 100px"
                    v-model="form.header.year"
                    :readonly="isReadonlyHeaderDate(props)"
                    :min="2000"
                    :max="new Date().getFullYear() + 100"
                    @focusout="
                      () => {
                        if (form.header.year < 2000) form.header.year = new Date().getFullYear();
                        if (form.header.year > new Date().getFullYear() + 100) form.header.year = new Date().getFullYear();
                      }
                    "
                  />&nbsp;년
                </div>
                <div v-else-if="type === 'month'" class="flex items-center"><vs-input type="number" class="ml-4 mr-2" style="width: 100px" v-model="form.header.month" :readonly="isReadonlyHeaderDate(props)" :min="1" :max="12" />월</div>
                <div v-else-if="type === 'half'" class="flex items-center">
                  <vs-select v-model="form.header.half" :disabled="isReadonlyHeaderDate(props)" autocomplete style="width: 100px">
                    <vs-select-item v-for="half in ['상반기', '하반기']" :text="half" :value="half" />
                  </vs-select>
                </div>
              </div>
            </div>
          </div>

          <!-- only title -->
          <div v-else id="print-label" class="h4 grid xl:flex gap-4" style="align-items: center">
            <p class="font-bold h4 flex" style="align-items: center">{{ tabItems[boardId].title || tab.label }}</p>
          </div>
        </template>

        <!-- 결재문서 상단 버튼들 -->
        <template #approval-right="props">
          <component
            v-for="(component, component_i) in writeApprovalButtons"
            :key="`approval-button-controls-${component_i}`"
            v-if="writeShouldShowRightComponent(component.shouldShow, props.mode, component.key)"
            :is="component.component"
            v-bind="component.props"
            @click="
              () => {
                component.emits.click(props.func);
              }
            "
            @select="
              () => {
                component.emits.select(props.func);
              }
            "
            @change="
              () => {
                component.emits.change(props.func);
              }
            "
            @input="
              () => {
                component.emits.input(props.func);
              }
            "
            @event="
              () => {
                component.emits.event(props.func);
              }
            "
            v-html="component.content"
          />
        </template>
        <template #body="props">
          <component
            ref="approvalDocument"
            :is="tabPageInfo.component"
            :tab="tab"
            :parentId="tab.parentId"
            :board="props.board"
            :permission="permission"
            :header="form.header"
            :oldForm="writeOldForm"
            :is-print="props.isPrint"
            :post="post"
            v-model="form.body"
            @setPrevBoardOptions="(value) => writeSetPrevBoardOptions(value)"
            @EnableSave="setEnableSave"
          />
          <FileDownload v-if="!props.isPrint && tab.visibleFileDownload" :isChange="isAdmin" />
        </template>
      </approval-document2>
      <normal-document
        v-else
        ref="refNormalDocument"
        :tab="tab"
        :use-finish-later="tab.useFinishLater"
        :created="useCreated"
        :header="form.header"
        :docProjectId="writeDocProjectId"
        :permission="permission"
        :usePrevPost="tab.usePrevPost"
        :prevPostButtonLabel="tab.prevPostButtonLabel"
        :writePrevBoardOptions="writePrevBoardOptions"
        :use-print="tab.usePrint === true || false"
        :use-print-title="typeof tab.usePrintTitle === 'boolean' ? tab.usePrintTitle : true"
        :use-delete="typeof tab.useDelete === 'boolean' ? tab.useDelete : true"
        :use-excel-download="tab.useExcelDownload"
        :print-label="tab.printLabel"
        :use-copy-button="tab.useCopyButton"
        @board="setBoard"
        @move="moveTabPage"
        @remove="writeOnClickNormalRemove"
        @save="writeOnClickNormalSave"
        @copy="copyOnClickNormal"
        @prevPost="writeOnClickPrevPost"
        @excelDownload="writeOnClickExcelDownload"
      >
        <template #header="props">
          <div v-if="tab.useHeaderDate" class="h4 grid xl:flex gap-4" style="align-items: center">
            <div id="print-label">
              <p class="font-bold h4 flex" style="align-items: center">{{ tabItems[boardId].title || tab.label }}</p>
            </div>

            <div v-if="tab.headerDateType === 'date'" class="flex items-center">
              <date-selector class="mr-2" style="width: 140px" v-model="form.header.date" :readonly="props.board.readonly || (tab.useHeaderDateEdit !== true && !!postId)" />
            </div>
            <div v-else-if="tab.headerDateType === 'year'" class="flex items-center">
              <vs-input
                type="number"
                class="ml-2"
                style="width: 100px"
                v-model="form.header.year"
                :readonly="props.board.readonly || (tab.useHeaderDateEdit !== true && !!postId)"
                :min="2000"
                :max="new Date().getFullYear() + 100"
                @focusout="
                  () => {
                    if (form.header.year < 2000) form.header.year = new Date().getFullYear();
                    if (form.header.year > new Date().getFullYear() + 100) form.header.year = new Date().getFullYear();
                  }
                "
              />&nbsp;년
            </div>
            <div v-else-if="tab.headerDateType === 'year-quarter'" class="flex items-center gap-2">
              <vs-input
                type="number"
                class="ml-2"
                style="width: 100px"
                v-model="form.header.year"
                :readonly="props.board.readonly || (tab.useHeaderDateEdit !== true && !!postId)"
                :min="2000"
                :max="new Date().getFullYear() + 100"
                @focusout="
                  () => {
                    if (form.header.year < 2000) form.header.year = new Date().getFullYear();
                    if (form.header.year > new Date().getFullYear() + 100) form.header.year = new Date().getFullYear();
                  }
                "
              />&nbsp;년

              <vs-input v-if="props.board.readonly || !!postId" type="number" style="width: 50px" v-model="form.header.quarter" :readonly="props.board.readonly || (tab.useHeaderDateEdit !== true && !!postId)" />
              <vs-select v-else v-model="form.header.quarter" autocomplete style="width: 50px">
                <vs-select-item v-for="quarter in ['1', '2', '3', '4']" :text="quarter" :value="quarter" />
              </vs-select>
              분기
            </div>
            <div v-else class="flex items-center">
              <vs-input
                type="number"
                class="mr-2"
                style="width: 100px"
                v-model="form.header.year"
                :readonly="props.board.readonly || (!tab.useHeaderDateEdit && !!postId)"
                :min="2000"
                :max="new Date().getFullYear() + 100"
                @focusout="
                  () => {
                    if (form.header.year < 2000) form.header.year = new Date().getFullYear();
                    if (form.header.year > new Date().getFullYear() + 100) form.header.year = new Date().getFullYear();
                  }
                "
              />년 <vs-input type="number" class="ml-4 mr-2" style="width: 100px" v-model="form.header.month" :readonly="props.board.readonly || (tab.useHeaderDateEdit !== true && !!postId)" :min="1" :max="12" />월
            </div>
          </div>
          <div v-else id="print-label" class="h4 grid xl:flex gap-4" style="align-items: center">
            <p class="font-bold h4 flex" style="align-items: center">{{ tabItems[boardId].title || tab.label }}</p>
          </div>
        </template>

        <!-- 상단 버튼들 -->
        <template #normal-board-right="props">
          <template v-for="(component, component_i) in writeNormalBoardButtons">
            <component v-if="writeShouldShowRightComponent(component.shouldShow, props.mode, component.key)" :is="component.tooltip ? 'vs-tooltip' : 'div'" :key="`buttons-tooltip-${component_i}`" :text="component.tooltip">
              <component
                :key="`normal-board-button-controls-${component_i}`"
                :is="component.component"
                class="w-full"
                :class="[component.icon ? 'custom-icon-size' : '', ...component.props.class]"
                v-bind="component.props"
                icon-pack="feather"
                :icon="component.icon"
                @click="
                  () => {
                    component.emits.click(props.func);
                  }
                "
                @select="
                  () => {
                    component.emits.select(props.func);
                  }
                "
                @change="
                  () => {
                    component.emits.change(props.func);
                  }
                "
                @input="
                  () => {
                    component.emits.input(props.func);
                  }
                "
                @event="
                  () => {
                    component.emits.event(props.func);
                  }
                "
                v-html="component.content"
              >
              </component>
            </component>
          </template>
        </template>
        <template #body="props">
          <component
            ref="normalDocumentComponent"
            :is="tabPageInfo.component"
            :tab="tab"
            :board="props.board"
            :permission="permission"
            :header="form.header"
            :oldForm="writeOldForm"
            :originForm="writeOriginForm"
            :is-print="props.isPrint"
            :docProjectId="writeDocProjectId"
            :post="post"
            :openType="props.openType"
            :funcRefreshButtons="refreshButtons"
            v-model="form.body"
            @saveButtonControl="(value) => writeSaveButtonControl(value)"
            @removeButtonControl="(value) => writeRemoveButtonControl(value)"
            @setPrevBoardOptions="(value) => writeSetPrevBoardOptions(value)"
            @EnableSave="setEnableSave"
          />
          <FileDownload v-if="!props.isPrint && tab.visibleFileDownload" :isChange="isAdmin" />
        </template>
      </normal-document>
    </div>

    <!-- page -->
    <div v-if="tab && this.tabPageInfo.pageType === 'page'">
      <component :is="tabPageInfo.component" :board="board" :header="form.header" :permission="permission" />
    </div>

    <!-- excel-upload -->
    <div v-if="tab && this.tabPageInfo.pageType === 'excel-upload'">
      <component :is="tabPageInfo.component" :board="board" :header="form.header" @customAlert="customAlert" @save="writeOnClickSaveMultiple" />
    </div>

    <!-- excel-upload-bulk -->
    <div v-if="tab && this.tabPageInfo.pageType === 'excel-upload-bulk'">
      <component :is="tabPageInfo.component" :board="board" :header="form.header" @customAlert="customAlert" @save="writeOnClickSaveMultiple" />
    </div>

    <!-- multiPrintPopup -->
    <popup-multi-print-viewer
      v-if="tab && this.tabPageInfo.pageType === 'list'"
      v-model="popup.multiPrintViewer"
      :boardId="boardId"
      :isApproval="tab.isApproval"
      :searchComponent="searchComponent"
      :nav="nav"
      :method="{
        getContent: this.getContent,
        getCreatedDate: this.getCreatedDate,
        getCompanyName: this.getCompanyName,
        getContentTargetList: this.getContentTargetList,
        onNameBlur: this.onNameBlur,
        getContentRiskList: this.getContentRiskList,
        getContentStringList: this.getContentStringList,
        getAuthUserInfo: this.getAuthUserInfo,
        comma: this.comma,
      }"
    ></popup-multi-print-viewer>

    <popup-load-prev-board v-if="popup.loadPrevBoard" v-model="popup.loadPrevBoard" :title="writePrevBoardOptions.title" :set-board-id="writePrevBoardOptions.boardId" @selectRowData="prevBoardSelectRow" />
    <!-- multiExcelDownloadPopup-->
    <popup-excel-download-viewer
      v-model="popup.multiExcelDownload"
      :boardId="tab.boardId"
      :isApproval="tab.isApproval"
      :searchComponent="searchComponent"
      :nav="nav"
      :method="{
        getContent: this.getContent,
        getCreatedDate: this.getCreatedDate,
        getCompanyName: this.getCompanyName,
        getContentTargetList: this.getContentTargetList,
        onNameBlur: this.onNameBlur,
        getContentRiskList: this.getContentRiskList,
        getContentStringList: this.getContentStringList,
        getAuthUserInfo: this.getAuthUserInfo,
        comma: this.comma,
      }"
    />
  </div>
</template>

<script>
/* eslint-disable brace-style */
import HttpRequest from "@/util/HttpRequest";
import moment from "moment";
import normalSearchNav from "@/views/boards/components";
import SearchNavItemTartgets from "@/views/boards/components/SearchNavItemTartgets.vue";
import tabItems from "@/views/boards/tab/tab-items";
import includeSinglePage from "@/views/boards/single-page/include";
import TableListPage from "@/views/list/TableListPage.vue";
import PopupExcelDownloadViewer from "@/popup/PopupExcelDownloadViewer.vue";
import PopupMultiPrintViewer from "@/popup/PopupMultiPrintViewer.vue";
import PopupLoadPrevBoard from "@/popup/PopupLoadPrevBoard.vue";
import NormalDocument from "@/views/write/document/NormalDocument.vue";
import ApprovalDocument2 from "@/views/write/document/ApprovalDocument2.vue";
import FileDownload from "@/components/FileDownload.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import ExcelJS from "exceljs";
import FieldTab from "./FieldTab.vue";

export default {
  components: {
    DateSelector,
    FileDownload,
    ApprovalDocument2,
    NormalDocument,
    PopupLoadPrevBoard,
    PopupMultiPrintViewer,
    PopupExcelDownloadViewer,
    SearchNavItemTartgets,
    TableListPage,
    FieldsList: () => import("@/views/fields/FieldsList.vue"),
    FieldTab,
  },
  watch: {
    __permission_boards(value) {
      this.initTab();
    },
    menuList() {
      // api 를 통해서 메뉴를 불러오기 때문에, beforeMount 에서 실행하면 menuList 가 비어있는 상태로 실행되어서
      // watch 를 사용하여 menuList 가 변경되면 실행하도록 한다.
      // router.push 를 통해서 이동할 때는 menuList 가 변하지 않기 때문에, beforeMount 에서 실행하도록 한다.
      this.initBreadcrumb();
      this.initTab();
    },
    tabList() {
      if (this.tabList.length > 0) {
        this.tab = this.tabList[this.currentTabIndex];
      }
    },
    tab(tab) {
      try {
        // 신규작성시 _cp_pid 가 있을 경우, 해당 게시물을 불러온다.
        const postId = this.postId || this.$route.query["_cp_pid"];
        if (postId) {
          if (tab.isApproval) {
            this.writeLoadApprovalPost(postId);
          } else {
            this.writeLoadBoardPost(postId);
          }
        }

        // 중요위험등록부에서 개정 했을 경우,
        const parentId = this.$route.query["_p_pid"];
        if (parentId) {
          this.tab.parentId = parentId;
          const strForm = localStorage.getItem(`tempResend_${this.boardId}_${parentId}`);
          if (strForm && typeof strForm === "string") {
            const form = JSON.parse(strForm);
            if (form.header) this.form.header = form.header;
            if (form.body) this.form.body = form.body;
          }
        }
      } catch (e) {}
    },
    writeOldForm: {
      immediate: true,
      deep: true,
      handler() {
        this.$store.commit("menu/SET_OLD_WRITE_FORM", this.writeOldForm ? JSON.stringify({ header: this.writeOldForm.header, body: this.writeOldForm.body }) : undefined);
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler() {
        this.$store.commit("menu/SET_WRITE_FORM", this.form ? JSON.stringify({ header: this.form.header, body: this.form.body }) : undefined);
      },
    },
  },
  computed: {
    isAdmin() {
      let isAdmin = false;
      const myInfo = this.$store.state.auth.userInfo;
      isAdmin = isAdmin || parseInt(myInfo.is_superuser) > 0;
      return isAdmin;
    },
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    isMobile() {
      return !(this.$store.state.verticalNavMenuWidth === "default");
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId ? this.$route.params.boardId : "";
    },
    boardSlug() {
      for (const tab of this.tabList) {
        if (`${tab.boardId}` === `${this.boardId}`) {
          return tab.slug;
        }
      }
      return "";
    },
    pageTitle() {
      return this.$store.state.menu.pageTitle;
    },
    postId() {
      return this.$route.params.postId ? this.$route.params.postId : "";
    },
    menuList() {
      return this.$store.state.menu.list;
    },
    currentMenu() {
      return this.__findNode(this.menuList, this.tabPageInfo.relatedPath);
    },
    tabPageInfo() {
      const pageType = this.$route.params.pageType ? this.$route.params.pageType : "list";
      const query = this.$route.query;
      const tabKey = `${this.boardId}_${pageType}`;

      const component = this.singlePage[pageType][tabKey.replaceAll("-", "")];
      const relatedPath = !pageType || pageType === "list" || pageType === "write" ? `/tab/${this.boardId}` : `/tab/${this.boardId}/${pageType}`;
      const tabItem = this.tabItems[this.boardId];
      return { pageType, query, tabKey, component, relatedPath, tabItem };
    },
    singlePage() {
      const list = includeSinglePage.list;
      const write = includeSinglePage.write;
      const page = includeSinglePage.page;
      const excelupload = includeSinglePage.excelUpload;
      const exceluploadbulk = includeSinglePage.excelUploadBulk;
      return { list, write, page, "excel-upload": excelupload, "excel-upload-bulk": exceluploadbulk };
    },
    /**
     * 현재 사용자가 프로젝트 관리자인지 확인합니다.
     * 프로젝트 관리자는 사업장을 등록/수정/삭제할 수 있습니다.
     * @returns {boolean}
     */
    isProjectManager() {
      const memberInfo = this.$store.state.member.memberInfo;
      if (memberInfo) {
        if (memberInfo.is_superuser) {
          return true;
        }

        const projects = memberInfo.projects;
        if (projects && projects.length > 0) {
          for (const project of projects) {
            if (`${project.id}` === `${this.projectId}`) {
              return project.pivot.is_manager;
            }
          }
        }
      }
      return false;
    },

    useCreated() {
      const _create = typeof this.tab.useCreate === "boolean" ? this.tab.useCreate : true;
      return _create && this.enableSave;
    },

    /**
     * ================================================================
     * pageType === 'list'
     * ================================================================
     */
    searchComponent() {
      return normalSearchNav[`NormalSearchNav${this.boardId}`];
    },

    // 리스트의 [신규작성] 작성 버튼 사용 여부
    listUseCreateButton() {
      const component = this.tabPageInfo.component || {};
      if (component.hideCreateButton) return !component.hideCreateButton();
      return this.projectId > 0;
    },

    // 리스트 상단 totalCount 영역에 표시할 보드별 커스텀 요약 컴포넌트
    listSummaryComponent() {
      const component = this.tabPageInfo.component;
      if (component && component.summaryComponent) {
        return component.summaryComponent;
      }
      return null;
    },

    listTableHeads() {
      if (this.tabPageInfo.component && this.tabPageInfo.component.getHead) {
        const heads = this.tabPageInfo.component.getHead(this.projectId);

        // table-list-page 에서 sort-key 로 사용하기 위해 keyName 을 추가해준다.
        if (heads.length > 0) {
          for (const head of heads) {
            head.keyName = head.name;
          }
        }

        if (parseInt(this.projectId) === 3) {
          if (heads.length > 0 && heads[0].name === "사업장명") {
            heads[0].name = "사업명";
          }
        }
        return heads;
      }

      return [];
    },

    /**
     * ================================================================
     * pageType === 'write'
     * ================================================================
     */
    // 작성페이지 헤더타입
    writeHeaderDateTypes() {
      return this.tab.headerDateType ? this.tab.headerDateType.split("|") : [];
    },
    writeNormalBoardButtonsTemplate() {
      const isAdmin = this.isAdmin;
      return {
        save: {
          shouldShow: { mode: "c|u", freePass: isAdmin ? 1 : 0, permission: "c|u", onlyWriter: true },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "filled" },
          key: "save", // 이 값은 수정하지 말아주세요!
          content: "저장", // 버튼 레이블
          emits: {
            click: (func) => {
              func.save();
            },
          },
        },
        move: {
          shouldShow: { mode: "c|u", freePass: 0, permission: "r", onlyWriter: false },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          key: "list", // 이 값은 수정하지 말아주세요!
          content: "목록", // 버튼 레이블
          emits: {
            click: (func) => {
              func.move();
            },
          },
        },
        delete: {
          shouldShow: { mode: "u", freePass: isAdmin ? 1 : 0, permission: "d", onlyWriter: true },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          key: "delete", // 이 값은 수정하지 말아주세요!
          content: "삭제", // 버튼 레이블
          emits: {
            click: (func) => {
              func.delete();
            },
          },
        },
        print: {
          shouldShow: { mode: "u", freePass: 0, permission: "r", onlyWriter: false },
          component: "vs-button",
          props: { class: "hidden lg:block", color: "secondary", type: "filled" },
          key: "print", // 이 값은 수정하지 말아주세요!
          content: "인쇄", // 버튼 레이블
          emits: {
            click: (func) => {
              func.print();
            },
          },
        },
        copy: {
          shouldShow: { mode: "u", freePass: 0, permission: "r", onlyWriter: false },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          key: "copy", // 이 값은 수정하지 말아주세요!
          content: "복사하기", // 버튼 레이블
          emits: {
            click: (func) => {
              func.copy();
            },
          },
        },
      };
    },
    writeApprovalButtonsTemplate() {
      const isAdmin = this.isAdmin;
      const statusCode = this.board.statusCode;
      const usedRevision = ["rejected"].indexOf(statusCode) > -1;
      const isApproved = ["approved"].indexOf(statusCode) > -1;
      const usedSave = !(this.$route.params.postId > 0) || (this.board.isWriter && this.board.statusCode === "temp");
      const usedDelete = ["approved", "ongoing", "rejected"].indexOf(statusCode) === -1 || this.getLevel === 1;
      const usedRecalled = this.$route.params.postId > 0 && this.board.isWriter && this.board.statusCode === "registered";

      return {
        isAdmin, // 관리자인지
        statusCode, // 결재문서의 진행상태
        copysave: {
          shouldShow: { mode: "u", freePass: isAdmin ? 1 : 0, permission: "u", onlyWriter: false, custom: isApproved },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          content: "개정", // revision 과 다름 (개선 후 데이터를 제거하여 신규문서로 저장)
          emits: {
            click: (func) => {
              func.copysave();
            },
          },
        },
        revision: {
          shouldShow: { mode: "u", freePass: 0, permission: "u", onlyWriter: false, custom: isApproved },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          content: "개정",
          emits: {
            click: (func) => {
              func.revision("개정 할 경우\n기존 문서는 유지되고, 내용이 복사되어 임시저장 됩니다.\n개정 하시겠습니까?");
            },
          },
        },
        resend: {
          shouldShow: { mode: "u", freePass: 0, permission: "u", onlyWriter: false, custom: usedRevision },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          content: "재상신",
          emits: {
            click: (func) => {
              func.resend("재상신 할 경우\n기존 문서는 유지되고, 내용이 복사되어 임시저장됩니다.\n재상신 하시겠습니까?");
            },
          },
        },
        save: {
          shouldShow: { mode: "c|u", freePass: 0, permission: "c|u", onlyWriter: true, custom: usedSave },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "filled" },
          content: "임시저장",
          emits: {
            click: (func) => {
              func.save();
            },
          },
        },
        recall: {
          shouldShow: { mode: "u", freePass: 0, permission: "d", onlyWriter: true, custom: usedRecalled },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "filled" },
          content: "결재문서 회수",
          emits: {
            click: (func) => {
              func.recall();
            },
          },
        },
        move: {
          shouldShow: { mode: "c|u", freePass: 0, permission: "r", onlyWriter: false },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          content: "목록",
          emits: {
            click: (func) => {
              func.move();
            },
          },
        },
        delete: {
          shouldShow: { mode: "u", freePass: isAdmin ? 1 : 0, permission: "d", onlyWriter: true, custom: usedDelete },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "border" },
          content: "삭제",
          emits: {
            click: (func) => {
              func.delete();
            },
          },
        },
        print: {
          shouldShow: { mode: "u", freePass: 0, permission: "r", onlyWriter: false },
          component: "vs-button",
          props: { class: "", color: "secondary", type: "filled" },
          content: "인쇄",
          emits: {
            click: (func) => {
              func.print();
            },
          },
        },
      };
    },

    getLevel() {
      const projects = this.$store.state.member.memberInfo.projects;
      if (projects) {
        const projectId = this.$route.params.proj_id;
        for (const proj of projects) {
          if (proj.id == projectId && proj.company.length > 0) {
            const path = proj.company[0].pivot.company_path;
            const lv = path.split("/").length - 1;
            return lv;
          }
        }
      }
    },

    hasPrefixFunc() {
      return this.tabPageInfo && this.tabPageInfo.component && this.tabPageInfo.component.prefixFunc;
    },

    __permission_boards() {
      return this.$store.state.member.memberInfo.boards;
    },
  }, // computed

  data() {
    return {
      tabItems,
      currentTabIndex: 0,
      subTabIndex: 0,
      showOnlyOne: false,
      tab: {},
      tabList: [],
      subTabList: [],
      enableSave: true,
      permission: { c: 0, r: 0, u: 0, d: 0, v: 0 },
      parentId: 0,
      listReloadKey: 0,

      board: {
        projectTitle: this.$store.state.project.projectInfo.field_name,
        writer: {}, //작성자 정보
        readonly: false, // 읽기전용인가
        isPrint: false,
        viewSaveBtn: true,
        viewRemoveBtn: true,
      },

      // list
      nav: {
        projectId: 0,
        dateTarget: "created_at",
        orderTarget: "created_at",
        orderDirection: "desc",
        date: { year: new Date().getFullYear(), month: "" },
        value: {},
        page: {},
        keyword: [], // 나중에 삭제해야함.
      },
      listCompanyList: [],

      // write / ExcelUpload / ExcelUploadBulk
      form: {
        header: {
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          date: new Date().format("yyyy-MM-dd"),
          half: new Date().getMonth() + 1 <= 6 ? "상반기" : "하반기", //반기
          quarter: `${Math.ceil((new Date().getMonth() + 1) / 3)}`, //분기
        },
        body: {},
      },
      post: {},
      writeOriginForm: null,
      writeOldForm: {},
      writeDocInfo: {},
      writeDocProjectId: {},
      writePrevBoardOptions: {
        boardId: Number(`${this.boardId}`),
        title: "이전 문서 불러오기",
        prevBoardSelectRow: undefined,
      },
      writeNormalBoardButtons: [],
      writeApprovalButtons: [],

      popup: {
        multiPrintViewer: false,
        loadPrevBoard: false,
        multiExcelDownload: false,
      },
    };
  },

  async created() {
    await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
    await this.$store.dispatch("project/LOAD_PROJECT", { project_id: this.projectId });
    await this.loadPermission();
    this.initNormalBoard();
    this.initApproval();
  },

  beforeMount() {
    this.nav.projectId = this.projectId;
    this.initBreadcrumb();
    this.initTab();
    this.initBackButtonRouteGuard();
  },

  async mounted() {
    window.addEventListener("refreshButtons", this.refreshButtons);
    // 창 닫힘/새로고침 시 변경사항 보호
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    // 접속한 유저 정보 초기화
    this.initUserInfo();

    if (this.postId) {
      // 게시글 상세보기 시 알림 개수 업데이트
      this.updateNotificationCount();
    } else if (this.tabPageInfo.pageType === "list") {
      await this.listLoadCompanyList();
    }
    this.loadBoardInfo().then(() => {
      this.writeOldForm = JSON.parse(JSON.stringify(this.form));
    });

    // Page만 사용되는 페이지면 page로 리디렉션
    if (this.tabItems[this.boardId] && this.tabItems[this.boardId].onlyPage && this.$route.params && this.$route.params.pageType !== "page") {
      this.$router.push({
        name: "page|tab",
        params: { pageType: "page", proj_id: this.projectId, boardId: this.boardId },
      });
    }
  },

  // 라우트 변경 시에도 알림 개수가 업데이트되도록 추가
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // 게시글 상세보기 페이지로 이동한 경우에만 알림 업데이트
      if (to.params.postId && to.params.pageType === "write") {
        vm.updateNotificationCount();
      }
    });
  },

  beforeDestroy() {
    window.removeEventListener("refreshButtons", this.refreshButtons);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  methods: {
    isReadonlyHeaderDate(props) {
      const statusCode = props.board.statusCode;
      if (statusCode) {
        const isTemp = statusCode === "temp";
        return props.board.readonly || (!isTemp && this.postId);
      }
      return !!(props.board.readonly || this.postId);
    },

    // 버튼 리프레시
    // 아래 방식으로 호출할 수 있다.
    // const event = new CustomEvent("refreshButtons");
    // window.dispatchEvent(event);
    refreshButtons() {
      this.initNormalBoardButtons();
      this.initApprovalButtons();
    },

    initUserInfo() {
      const memberInfo = this.$store.state.member.memberInfo;
      let section = this.$store.state.auth.userInfo.section;
      if (memberInfo.virtual_company) {
        const virtualCompany = memberInfo.virtual_company;
        const fullName = virtualCompany.full_name;
        if (Array.isArray(fullName) && fullName.length > 0) {
          const index = Math.max(0, fullName.length - 2);
          section = fullName[index];
        }
      }

      this.board.writer = {
        id: this.$store.state.auth.userInfo.id,
        name: this.$store.state.auth.userInfo.name,
        company_id: this.$store.state.auth.userInfo.company_id,
        department: this.$store.state.auth.userInfo.department,
        section,
        rank: this.$store.state.auth.userInfo.rank,
      };
    },

    initNormalBoard() {
      this.initNormalBoardButtons();
    },
    initNormalBoardButtons() {
      if (this.$refs.normalDocumentComponent && this.$refs.normalDocumentComponent.setNormalBoardButtons) {
        this.writeNormalBoardButtons = this.$refs.normalDocumentComponent.setNormalBoardButtons(this.writeNormalBoardButtonsTemplate);
        for (const component of this.writeNormalBoardButtons) {
          const events = ["click", "input", "select", "change", "event", "copy"];
          for (const event of events) {
            if (!component.emits[event]) {
              component.emits[event] = () => {};
            }
          }
        }
      }
    },
    initApproval() {
      this.initApprovalButtons();
    },
    initApprovalButtons() {
      if (this.$refs.approvalDocument && this.$refs.approvalDocument.setApprovalButtons) {
        this.writeApprovalButtons = this.$refs.approvalDocument.setApprovalButtons(this.writeApprovalButtonsTemplate);
        for (const component of this.writeApprovalButtons) {
          const events = ["click", "input", "select", "change", "event"];
          for (const event of events) {
            if (!component.emits[event]) {
              component.emits[event] = () => {};
            }
          }
        }
      }
    },
    /**
     * 게시판 상세 정보를 불러온다
     * this.$store.state.board.boardData 변수에서 읽어올 수 있다.
     */
    async loadBoardInfo() {
      await this.$store.dispatch("board/LOAD_BOARD", { boardId: this.boardSlug });
    },
    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
    onClickMoveBoard() {
      this.moveTabPage("write");
    },
    moveTabPage(pageType, addQuery = {}) {
      // 작성페이지 url query 에 _t_back 사용시,
      // 목록으로 이동하는 액션 (목록, 저장, 수정, 승인 등) 시 _t_back 에 입력된 페이지로 이동
      // » (dksteel) 중요위험등록부( 816) 에서 위험성평가(813, 814) 페이지의 개정 페이지로 이동시킨 뒤, 다시 돌아올 목적으로 작성함
      // ?_t_back=813
      // ?_t_back=816/page
      const _t_back = this.$route.query._t_back;
      if (_t_back) {
        const pages = _t_back.split("/");
        const boardId = pages[0];
        const pageType = pages.length > 0 ? pages[1] : "list";
        const query = this.$route.query;
        delete query._t_back;
        if (_t_back.indexOf("page")) {
          this.$router.push({ name: "page|tab", params: { pageType, proj_id: `${this.projectId}`, boardId }, query });
        } else {
          this.$router.push({ name: "list|tab", params: { pageType, proj_id: `${this.projectId}`, boardId }, query });
        }
        return;
      }

      // close back
      // _c_back 가 있을 경우, 창을 닫는다.
      const _c_back = this.$route.query._c_back;
      if (_c_back) {
        window.close();
      }

      // all url back
      // _a_back 가 있을 경우, 해당 페이지로 이동한다.
      const _a_back = this.$route.query._a_back;
      if (_a_back) {
        this.$router.push({ path: `${_a_back}` });
        return;
      }

      const proj_id = `${this.projectId}`;
      const boardId = `${this.boardId}`;
      const query = { ...this.$route.query, ...addQuery };
      if (pageType === "list") {
        // 목록으로 이동할 경우 _cp_pid 를 제거 한다.
        delete query._cp_pid;
        this.$router.push({ name: "list|tab", params: { pageType, proj_id, boardId }, query });
      } else if (pageType === "write") {
        this.$router.push({ name: "write|tab", params: { pageType, proj_id, boardId }, query });
      } else if (pageType === "page") {
        this.$router.push({ name: "page|tab", params: { pageType, proj_id, boardId }, query });
      }
    },
    setEnableSave(enableSave) {
      this.enableSave = enableSave;
    },
    setBoard(board, approval) {
      this.board = board;
      this.approval = approval;
      this.initApprovalButtons();
    },
    getContent(data) {
      if (data.post_content) {
        const content = JSON.parse(data.post_content);
        return content.body;
      }
    },
    getHeader(data) {
      if (data.post_content) {
        const content = JSON.parse(data.post_content);
        return content.header;
      }
    },
    getCreatedDate(data) {
      if (data === "") return "";
      return moment(data).format("YYYY-MM-DD");
    },
    getCompanyName(comId) {
      for (const com of this.listCompanyList) {
        if (com.id === comId) {
          return com.com_name;
        }
      }
    },
    onNameBlur(name) {
      const length = name.length;
      if (length > 2) {
        const first = name[0];
        const last = name[name.length - 1];
        let star = "";
        for (let index = 0; index < length - 2; index++) {
          star += "*";
        }
        name = first + star + last;
      } else if (length === 2) {
        const first = name[0];
        name = `${first}*`;
      }
      return name;
    },
    getContentRiskList(list) {
      let result = "";
      for (const data of list) {
        if (data.input_3) {
          if (typeof data.input_3 === "string") {
            if (!result.includes(data.input_3)) {
              result += `${data.input_3},`;
            }
          }

          if (typeof data.input_3 === "object") {
            const temp = data.input_3.toString().split(",");
            for (const t of temp) {
              if (t && !result.includes(t)) {
                result += `${t},`;
              }
            }
          }
        }
      }
      return result.substring(0, result.length - 1);
    },
    getAuthUserInfo() {
      return this.$store.state.auth.userInfo;
    },
    getContentTargetList(list) {
      let result = "";
      for (const data of list) {
        if (!result.includes(data.target)) {
          result += `${data.target},`;
        }
      }
      return result.substring(0, result.length - 1);
    },
    getContentStringList(list) {
      let result = "";
      for (const data of list) {
        if (!result.includes(data)) {
          result += `${data},`;
        }
      }
      return result.substring(0, result.length - 1);
    },
    getContentRiskTypeList(list) {
      let result = "";
      for (const data of list) {
        if (!result.includes(data.input_1)) {
          result += `${data.input_1},`;
        }
      }
      return result.substring(0, result.length - 1);
    },
    comma(str) {
      return `${str}`.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // TODO : ======================== API ======================== //
    async loadPermission() {
      if (Number.isInteger(parseInt(this.boardId))) {
        let boards = this.$store.state.member.memberInfo.boards;
        if (!boards) {
          await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
          boards = this.$store.state.member.memberInfo.boards;
        }

        let board = boards.find((b) => parseInt(b.id) === parseInt(this.boardId));
        if (!!board && !!board.pivot) {
          if (board.pivot.project_id) {
            // 프로젝트에 맞는 권한이 있는지 다시 조사한다
            board = boards.find((b) => parseInt(b.pivot.board_id) === parseInt(this.boardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
            this.permission = { ...board.pivot };
          } else {
            this.permission = { ...board.pivot };
          }
        }
      }
    },

    // TODO : ======================== Init ======================== //
    initBreadcrumb() {
      const breadcrumb = [{ title: "Home" }];
      if (this.currentMenu && this.currentMenu.path && this.currentMenu.path.length > 0) {
        for (const p of this.currentMenu.path) {
          breadcrumb.push({ title: p, note: this.currentMenu.note });
        }
      }

      if (breadcrumb.length > 1) {
        breadcrumb[breadcrumb.length - 1].active = true;
      }

      this.$store.commit("menu/UPDATE_PAGE_LIST", breadcrumb);
    },
    initTab() {
      this.tabList = [];
      this.subTabList = [];
      // 현재 접속한 페이지의 형제 메뉴를 찾아 탭에 추가한다.
      // relatedPath = '/tab/1' or '/tab/1/write' or '/tab/1/page'
      const relatedPath = this.tabPageInfo.relatedPath;
      const currentMenuInfo = this.__findCurrentMenuInfo(this.menuList, relatedPath);
      const parentSiblings = (currentMenuInfo && currentMenuInfo.parentSiblings) || [];
      const siblings = (currentMenuInfo && currentMenuInfo.siblings) || [];

      if (currentMenuInfo) {
        const isTabCurrentMenu = (currentMenuInfo && currentMenuInfo.data && (currentMenuInfo.data.visible === "tab" || currentMenuInfo.data.visible === "tab_menu")) || false;
        const isTabParentMenu = (currentMenuInfo && currentMenuInfo.parent && (currentMenuInfo.parent.visible === "tab" || currentMenuInfo.parent.visible === "tab_menu")) || false;

        // 현재 페이지가 탭 메뉴인 경우
        if (isTabCurrentMenu) {
          this.showOnlyOne = true; // 현재 페이지가 탭이므로, 탭을 무조건 표기해주기 위해서 true 로 설정한다.
          // 부모 페이지가 탭 메뉴인 경우
          if (isTabParentMenu) {
            // 이중 탭인 경우
            // 부모 탭
            if (parentSiblings) {
              for (const node of parentSiblings) {
                if (node.visible === "tab" || node.visible === "tab_menu") {
                  const _url_path = node.url.split("/");
                  const __index_tab = _url_path.indexOf("tab");
                  const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
                  const boardId = _url_path[__index_tab + 1];
                  const tabLabel = node.name;
                  this.addTab(pageType, boardId, tabLabel);
                }
              }
            }
            // 자식 탭
            if (siblings) {
              for (const node of siblings) {
                if (node.visible === "tab" || node.visible === "tab_menu") {
                  const _url_path = node.url.split("/");
                  const __index_tab = _url_path.indexOf("tab");
                  const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
                  const boardId = _url_path[__index_tab + 1];
                  const tabLabel = node.name;
                  this.addSubTab(pageType, boardId, tabLabel);
                }
              }
            }
          } else if (currentMenuInfo.siblings && currentMenuInfo.siblings.length > 0) {
            for (const node of currentMenuInfo.siblings) {
              if (node.note) {
                try {
                  const note = JSON.parse(node.note);
                  if (note.onlyAdmin && !this.isAdmin) {
                    continue;
                  }
                } catch (e) {}
              }

              if (node.visible === "tab" || node.visible === "tab_menu") {
                const _url_path = node.url.split("/");
                const __index_tab = _url_path.indexOf("tab");
                const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
                const boardId = _url_path[__index_tab + 1];
                const tabLabel = node.name;
                this.addTab(pageType, boardId, tabLabel);
              }
            }
          } else {
            const node = currentMenuInfo.data;
            const _url_path = node.url.split("/");
            const __index_tab = _url_path.indexOf("tab");
            const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
            const boardId = _url_path[__index_tab + 1];
            const tabLabel = node.name;
            this.addTab(pageType, boardId, tabLabel);
          }
        }

        // 현재 페이지가 탭이 아닌 경우
        else {
          const node = currentMenuInfo.data;
          if (node.visible === "menu") {
            if (node.url === relatedPath) {
              // list, write, page 를 구분하여 탭에 추가한다.
              const _url_path = node.url.split("/");
              const __index_tab = _url_path.indexOf("tab");
              const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
              const boardId = _url_path[__index_tab + 1];
              const tabLabel = node.name;
              this.addTab(pageType, boardId, tabLabel);
            } else if (node.url.indexOf("/page") >= 0 && node.url.indexOf(this.tabPageInfo.relatedPath) >= 0) {
              // page -> write 로 진입한 경우
              const _url_path = relatedPath.split("/");
              const __index_tab = _url_path.indexOf("tab");
              const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
              const boardId = _url_path[__index_tab + 1];
              this.addTab(pageType, boardId);
            }
          }
        }
      } else {
        // 현재 페이지가 메뉴에 없는 경우
        const _url_path = relatedPath.split("/");
        const __index_tab = _url_path.indexOf("tab");
        const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
        const boardId = _url_path[__index_tab + 1];
        this.addTab(pageType, boardId);
      }

      // const nodeList = this.__findSiblingsNodeList(this.menuList, relatedPath);
      //
      // this.tabList = [];
      // if (nodeList.length > 0) {
      //   for (const node of nodeList) {
      //     // 탭이 표기되지 않는 하나의 페이지인 경우
      //     if (node.visible === "menu") {
      //       if (node.url === relatedPath) {
      //         // list, write, page 를 구분하여 탭에 추가한다.
      //         const _url_path = node.url.split("/");
      //         const __index_tab = _url_path.indexOf("tab");
      //         const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
      //         const boardId = _url_path[__index_tab + 1];
      //         const tabLabel = node.name;
      //         this.addTab(pageType, boardId, tabLabel);
      //         break;
      //       }
      //       else if (node.url.indexOf("/page") >= 0 && node.url.indexOf(this.tabPageInfo.relatedPath) >= 0) {
      //         // page -> write 로 진입한 경우
      //         const _url_path = relatedPath.split("/");
      //         const __index_tab = _url_path.indexOf("tab");
      //         const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
      //         const boardId = _url_path[__index_tab + 1];
      //         this.addTab(pageType, boardId);
      //       }
      //     }
      //
      //     if (node.visible === "tab" || node.visible === "tab_menu") {
      //       this.showOnlyOne = true;
      //
      //       const _url_path = node.url.split("/");
      //       const __index_tab = _url_path.indexOf("tab");
      //       const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
      //       const boardId = _url_path[__index_tab + 1];
      //       const tabLabel = node.name;
      //       this.addTab(pageType, boardId, tabLabel);
      //     }
      //   }
      // } else {
      //   // 현재 페이지가 메뉴에 없는 경우
      //   const _url_path = relatedPath.split("/");
      //   const __index_tab = _url_path.indexOf("tab");
      //   const pageType = _url_path.length > __index_tab + 2 ? _url_path[__index_tab + 2] : "list";
      //   const boardId = _url_path[__index_tab + 1];
      //   this.addTab(pageType, boardId);
      // }
      //

      // 현재 페이지의 tabIndex 를 찾는다.
      let tabKey = this.tabPageInfo.tabKey.replaceAll("write", "list").replaceAll("-", "");
      if (this.tabPageInfo.tabItem && this.tabPageInfo.tabItem.useRevision) {
        // 개정 기능을 사용할 때
        tabKey += "_revision:";
        tabKey += typeof this.tabPageInfo.query.isParent === "undefined" ? "" : this.tabPageInfo.query.isParent;
      }

      //this.currentTabIndex = -1;
      for (let i = 0; i < this.tabList.length; i++) {
        if (this.tabList[i].tabKey === tabKey) {
          this.currentTabIndex = i;
          break;
        }
      }

      // 현재 페이지의 tabIndex 가 없는 경우, subTabList 에서 찾는다.
      this.subTabIndex = -1;
      for (let i = 0; i < this.subTabList.length; i++) {
        if (this.subTabList[i].tabKey === tabKey) {
          this.subTabIndex = i;
          break;
        }
      }

      // this.subTabIndex > -1 인 경우, 이중 탭 페이지이므로, 상위 탭의 index 를 찾는다.
      if (this.subTabIndex > -1) {
        for (let i = 0; i < this.tabList.length; i++) {
          for (const subTab of this.subTabList) {
            if (subTab.tabKey === this.tabList[i].tabKey) {
              this.currentTabIndex = i;
              break;
            }
          }
        }
      }
    },
    initBackButtonRouteGuard() {
      const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
        let allowNavigate = true;
        if (this.tabPageInfo.pageType === "write") {
          const hasChanges = JSON.stringify(this.writeOldForm || {}) !== JSON.stringify(this.form || {});
          allowNavigate = !hasChanges || confirm("저장하지 않은 데이터는 삭제됩니다. 닫으시겠습니까?");
        }
        if (allowNavigate) {
          document.querySelectorAll(".con-vs-popup").forEach((el) => {
            el.style.display = "none";
          });
          next(true);
        } else {
          // 이동을 명시적으로 취소하여 주소 표시줄이 바뀌지 않도록 처리
          next(false);
        }
      });
      this.$once("hook:destroyed", () => {
        backButtonRouteGuard();
      });
    },

    // 브라우저 창 닫힘/새로고침 보호
    handleBeforeUnload(e) {
      try {
        if (this.tabPageInfo && this.tabPageInfo.pageType === "write") {
          const hasChanges = JSON.stringify(this.writeOldForm || {}) !== JSON.stringify(this.form || {});
          if (hasChanges) {
            e.preventDefault();
            e.returnValue = "변경사항이 저장되지 않을 수 있습니다."; // 일부 브라우저는 빈 문자열이어도 동작
            return "변경사항이 저장되지 않을 수 있습니다.";
          }
        }
      } catch (err) {}
      return undefined;
    },

    // 입력한 페이지의 메뉴 정보를 찾는다.
    __findCurrentMenuInfo(menuList, relatedPath, parent, parentSiblings) {
      if (menuList && menuList.length > 0) {
        for (const menu of menuList) {
          if (menu.url && menu.url.indexOf(relatedPath) > -1) {
            if (menu.submenu) {
              // 현재 페이지의 메뉴 정보를 찾았다면, 하위 메뉴에서 같은 주소가 있는지 찾는다.
              const node = this.__findCurrentMenuInfo(menu.submenu, relatedPath, menu, menuList);
              if (node) {
                return node;
              }
            } else {
              // 최종적으로 찾은 메뉴 정보를 반환한다.
              return {
                data: menu,
                siblings: menuList,
                parent,
                parentSiblings,
              };
            }
          } else if (menu.submenu) {
            const node = this.__findCurrentMenuInfo(menu.submenu, relatedPath, menu, menuList);
            if (node) {
              return node;
            }
          }
        }
      }
      return undefined;
    },

    __findSiblingsNodeList(menuList, relatedPath) {
      if (menuList && menuList.length > 0) {
        for (const menu of menuList) {
          if (menu.url && menu.url.indexOf(relatedPath) > -1) {
            if (menu.submenu) {
              const list = this.__findSiblingsNodeList(menu.submenu, relatedPath);
              if (list.length > 0) {
                return list;
              }
            }
            return menuList;
          } else if (menu.submenu) {
            const list = this.__findSiblingsNodeList(menu.submenu, relatedPath);
            if (list.length > 0) {
              return list;
            }
          }
        }
      }
      return [];
    },
    __findNode(menuList, relatedPath) {
      if (menuList && menuList.length > 0) {
        for (const menu of menuList) {
          if (menu.url && menu.url.indexOf(relatedPath) > -1) {
            return menu;
          } else if (menu.submenu) {
            const node = this.__findNode(menu.submenu, relatedPath);
            if (node) {
              return node;
            }
          }
        }
      }
      return null;
    },

    // ======================== TAB ======================== //
    addTab(pageType, boardId, tabLabel) {
      // 권한체크
      if (!this.$store.state.member.memberInfo.boards) return;
      const myBoards = this.$store.state.member.memberInfo.boards;
      const find = myBoards && myBoards.find((board) => `${board.pivot.project_id}` === `${this.projectId}` && `${board.pivot.board_id}` === `${boardId}`);
      if (!find || find.pivot.v === 0) return;

      if (pageType === "list" || pageType === "write") {
        this.addListTab(this.tabList, boardId, tabLabel);
      } else if (pageType === "page") {
        this.addPageTab(this.tabList, boardId, tabLabel);
      } else if (pageType === "excel-upload") {
        this.addExcelUploadTab(this.tabList, boardId, tabLabel);
      } else if (pageType === "excel-upload-bulk") {
        this.addExcelUploadBulkTab(this.tabList, boardId, tabLabel);
      }
    },
    addSubTab(pageType, boardId, tabLabel) {
      if (pageType === "list" || pageType === "write") {
        this.addListTab(this.subTabList, boardId, tabLabel);
      } else if (pageType === "page") {
        this.addPageTab(this.subTabList, boardId, tabLabel);
      } else if (pageType === "excel-upload") {
        this.addExcelUploadTab(this.subTabList, boardId, tabLabel);
      } else if (pageType === "excel-upload-bulk") {
        this.addExcelUploadBulkTab(this.subTabList, boardId, tabLabel);
      }
    },
    addListTab(tabList, _boardId, tabLabel) {
      const boardId = _boardId.split("?")[0];
      const queryString = _boardId.split("?")[1];
      const query = queryString ? JSON.parse(`{"${decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`) : undefined;
      const slug = `board-${boardId}`;
      const item = this.tabItems[boardId];
      let tabKey = `${boardId}_list`;
      if (item && item.useRevision) {
        // 개정 기능을 사용할 때
        tabKey += "_revision:";
        tabKey += query && typeof query.isParent !== "undefined" ? query.isParent : "";
      }
      if (item) {
        tabList.push({ pageType: "list", ...item, tabKey, boardId, slug, query, label: tabLabel || item.label });
      }
    },
    addPageTab(tabList, _boardId, tabLabel) {
      const boardId = _boardId.split("?")[0];
      const queryString = _boardId.split("?")[1];
      const query = queryString ? JSON.parse(`{"${decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`) : undefined;
      const tabKey = `${boardId}_page`;
      const slug = `board-${boardId}`;
      const item = this.tabItems[boardId];
      if (item) {
        tabList.push({ pageType: "page", ...item, tabKey, boardId, slug, query, label: tabLabel || item.label });
      }
    },
    addExcelUploadTab(tabList, _boardId, tabLabel) {
      const boardId = _boardId.split("?")[0];
      const queryString = _boardId.split("?")[1];
      const query = queryString ? JSON.parse(`{"${decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`) : undefined;
      const tabKey = `${boardId}_excelupload`;
      const slug = `board-${boardId}`;
      const item = this.tabItems[boardId];
      if (item) {
        tabList.push({ pageType: "excel-upload", ...item, tabKey, boardId, slug, query, label: tabLabel || item.label });
      }
    },
    addExcelUploadBulkTab(tabList, _boardId, tabLabel) {
      const boardId = _boardId.split("?")[0];
      const queryString = _boardId.split("?")[1];
      const query = queryString ? JSON.parse(`{"${decodeURI(queryString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`) : undefined;
      const tabKey = `${boardId}_exceluploadbulk`;
      const slug = `board-${boardId}`;
      const item = this.tabItems[boardId];
      if (item) {
        tabList.push({
          pageType: "excel-upload-bulk",
          ...item,
          tabKey,
          boardId,
          slug,
          query,
          label: tabLabel || item.label,
        });
      }
    },
    moveTab(index, tab) {
      if (index === this.currentTabIndex) return;

      const boardId = tab.boardId;
      const pageType = tab.pageType;
      const query = tab.query;
      this.$router.push({
        name: this.projectId ? `${pageType}|tab` : `root|${pageType}|tab`,
        params: { boardId, pageType },
        query,
      });
    },

    moveSubTab(index, tab) {
      if (index === this.subTabIndex) return;

      const boardId = tab.boardId;
      const pageType = tab.pageType;
      const query = tab.query;
      this.$router.push({
        name: this.projectId ? `${pageType}|tab` : `root|${pageType}|tab`,
        params: { boardId, pageType },
        query,
      });
    },

    // ======================== LIST ======================== //
    __checkCreatable(list) {
      const nowMonth = new Date().getMonth() + 1;
      let maxMonth = 0;
      for (const data of list) {
        if (data.user.company_id === this.myInfo.company_id) {
          if (maxMonth < Number(`${this.getHeader(data).month}`)) {
            maxMonth = Number(`${this.getHeader(data).month}`);
          }
        }
      }
      return maxMonth < nowMonth;
    },
    listOnLoadData(list) {
      if (this.boardId === 86 || this.boardId === 106 || this.boardId === 154 || this.boardId === 155 || this.boardId === 250) {
        this.create = this.__checkCreatable(list);
      }
      this.listReloadKey++;
    },
    async listLoadCompanyList() {
      const projectId = this.projectId;

      if (this.tab && this.tab.useListGetCompanyName) {
        const page = 1;
        const perPage = 10000;
        await this.$store.dispatch("company/LOAD_COMPANY_LIST", {
          page,
          perPage,
          projectId,
        });
        this.listCompanyList = this.$store.state.company.companyList;
      }

      await this.$store.dispatch("company/LOAD_COMPANY_TREE", { projectId });
    },
    listGetTableColumn(index, value) {
      const func = {
        getContent: this.getContent,
        getCreatedDate: this.getCreatedDate,
        getCompanyName: this.getCompanyName,
        getContentTargetList: this.getContentTargetList,
        onNameBlur: this.onNameBlur,
        getContentRiskList: this.getContentRiskList,
        getContentStringList: this.getContentStringList,
        getAuthUserInfo: this.getAuthUserInfo,
        comma: this.comma,
      };

      if (this.tabPageInfo && this.tabPageInfo.component) {
        const __getCol = (index, value, func) => {
          const head = this.tabPageInfo.component.getHead()[index];
          const keys = head.key.split(".");

          const keysLength = keys.length;
          if (keysLength === 1) {
            if (keys[0] === "created_at") {
              return func.getCreatedDate(value.created_at);
            }
          }

          if (keysLength > 1) {
            const content = JSON.parse(value.post_content);
            const getData = (keys) => {
              let data = content;
              for (let i = 0; i < keys.length; i++) {
                data = data[keys[i]] || "";
              }
              return data;
            };
            return getData(keys);
          }
        };

        if (this.tabPageInfo.component.getCol) {
          return this.tabPageInfo.component.getCol(index, value, {
            ...func,
            _getCol: __getCol,
          });
        } else {
          const data = __getCol(index, value, func);
          if (Array.isArray(data)) {
            return data.join(", ");
          }
          return data;
        }
      }
      return "";
    },
    listGetRowStyle(value) {
      if (this.tabPageInfo && this.tabPageInfo.component && this.tabPageInfo.component.getRowStyle) {
        return this.tabPageInfo.component.getRowStyle(value);
      }
      return null;
    },
    listGetRowClass(value) {
      if (this.tabPageInfo && this.tabPageInfo.component && this.tabPageInfo.component.getRowClass) {
        return this.tabPageInfo.component.getRowClass(value);
      }
      return null;
    },
    listAddRowClassStyles() {
      if (this.tabPageInfo && this.tabPageInfo.component && this.tabPageInfo.component.addRowClassStyles) {
        return this.tabPageInfo.component.addRowClassStyles();
      }
      return null;
    },
    listOnClickMultiPrint() {
      this.popup.multiPrintViewer = true;
    },
    listOnChangeSearch(value) {
      this.nav.value = value;
    },

    // TODO : ======================== WRITE / excel_upload / excel_upload_bulk ======================== //
    __maskName(name) {
      name = `${name}`.trim();
      // 이름이 2개 이상의 단어로 이루어져 있는 경우
      if (name.includes(" ")) {
        // 이름을 공백으로 분리
        const names = name.split(" ");
        // 성은 맨 뒤 단어로 판단
        const lastName = names[names.length - 1];
        // 나머지 이름에 대해 마스킹 처리
        const otherNames = names.slice(0, -1).map((n) => n.substring(0, Math.ceil(n.length / 2)) + "*".repeat(Math.floor(n.length / 2)));
        return [...otherNames, lastName].join(" ");
      }

      // 이름이 1개의 단어로 이루어져 있는 경우
      const length = name.length;
      if (length === 1) {
        return "*";
      } else if (length === 2) {
        return `${name[0]}*`;
      } else {
        return name[0] + "*".repeat(length - 2) + name[length - 1];
      }
    },
    __removeComma(str) {
      if (str) {
        const noneComma = `${str}`.replaceAll(",", "");
        return `${Number(noneComma)}`;
      }
      return "0";
    },
    /** 암호화 저장 관련 */
    __writeLoadApprovalPostContent(form) {
      const body = form.body;
      if (this.boardId === 117) {
        const targetKeys = [
          "inputC_1",
          "inputC_2",
          "inputC_4",
          "inputC_5",
          "inputC_7",
          "inputC_8",
          "inputC_9",
          "inputC_10",
          "inputC_11",
          "inputC_12",
          "inputC_13",
          "inputC_14",
          "inputC_15",
          "inputC_16",
          "inputC_17",
          "inputC_18",
          "inputC_19",
          "inputC_20",
          "inputD_1",
          "inputD_2",
          "inputD_3",
          "inputD_4",
          "inputD_5",
          "inputD_6",
          "inputD_7",
          "inputE_1",
          "inputE_2",
          "inputE_3",
          "inputE_4",
        ];
        for (const key of targetKeys) {
          if (typeof form.enc !== "undefined" && typeof form.enc[key] !== "undefined") {
            body[key] = form.enc[key];
          }
        }
      } else if (this.boardId === 126) {
        let i = 0;
        if (body.group1 && body.group1.length > 0) {
          for (i = 0; i < body.group1.length; i++) {
            const data = body.group1[i];
            data.name = form.enc.group1[i].name;
            data.phone = form.enc.group1[i].phone;
          }
        }

        i = 0;
        if (body.group2 && body.group2.length > 0) {
          for (i = 0; i < body.group2.length; i++) {
            const data = body.group2[i];
            data.name = form.enc.group2[i].name;
            data.phone = form.enc.group2[i].phone;
          }
        }
      } else if (this.boardId === 136) {
        let i = 0;
        if (body.group1 && body.group1.length > 0) {
          for (i = 0; i < body.group1.length; i++) {
            const data = body.group1[i];
            data.name = form.enc.group1[i].name;
            data.phone = form.enc.group1[i].phone;
          }
        }

        i = 0;
        if (body.group2 && body.group2.length > 0) {
          for (i = 0; i < body.group2.length; i++) {
            const data = body.group2[i];
            data.name = form.enc.group2[i].name;
            data.phone = form.enc.group2[i].phone;
          }
        }
      } else if (this.boardId === 138) {
        let i = 0;
        if (body.group1 && body.group1.length > 0) {
          for (i = 0; i < body.group1.length; i++) {
            const data = body.group1[i];
            data.name = form.enc.group1[i].name;
            data.phone = form.enc.group1[i].phone;
          }
        }

        i = 0;
        if (body.group2 && body.group2.length > 0) {
          for (i = 0; i < body.group2.length; i++) {
            const data = body.group2[i];
            data.name = form.enc.group2[i].name;
            data.phone = form.enc.group2[i].phone;
          }
        }
      } else if (this.boardId === 141) {
        let i = 0;
        if (body.group1 && body.group1.length > 0) {
          for (i = 0; i < body.group1.length; i++) {
            const data = body.group1[i];
            data.name = form.enc.group1[i].name;
            data.phone = form.enc.group1[i].phone;
          }
        }
      }
      return form;
    },
    __writeSaveApprovalPostContent(form) {
      const body = form.body;

      return form;
    },
    async writeLoadApprovalPost(postId) {
      const boardSlug = this.boardSlug;
      let post = {};
      await this.$store.dispatch("approval/LOAD_POST", { boardSlug, postId }).then((value) => {
        post = value.post;
        this.post = post;
        this.writeDocProjectId = post.project_id;
        return value;
      });

      if (!this.$store.state.approval.postInfo) {
        alert("열람 권한이 없습니다.");
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        this.moveTabPage("list");
        return;
      }

      const approvalDocument = this.$refs.approvalDocument;

      let postContent = post.post_content;
      if (approvalDocument && approvalDocument.prefixSetPostContent) {
        // post 를 읽어온 뒤에 this.form = JSON.parse(post.post_content); 하기전 값에 호출한다.
        // 전달 받은 post.post_content 와 같은 형태로 return 해줘야 한다.
        postContent = await approvalDocument.prefixSetPostContent(post);
      }

      // 내용
      if (postContent) {
        let form = JSON.parse(postContent);

        if (!form.body["fileList"]) {
          form.body.fileList = [];
        }
        if (!form.body["formList"]) {
          form.body.formList = [];
        }

        // enc 내용을 본문에 추가하기
        form = this.__writeLoadApprovalPostContent(form);
        this.form = form;

        // post 를 불러온 후에 호출한다.
        if (approvalDocument.initForm) {
          approvalDocument.initForm(form);
        }
      }
      this.writeOldForm = JSON.parse(JSON.stringify(this.form));

      this.tab.parentId = post.post_parent_id;

      // 결재프로세스
      this.writeDocInfo = this.$store.state.approval.document;

      this.writeDocInfo.slug = this.boardSlug;

      // NotificationDropDown 컴포넌트의 알림 개수 업데이트
      this.updateNotificationCount();
    },
    async writeSaveApprovalPost(type, approvalLine, documentCompany /*처리부서*/, referenceCompanies, callback) {
      this.loading(true);

      try {
        const approvalDocument = this.$refs["approvalDocument"];
        if (
          approvalDocument.validate &&
          // 임시저장인 경우에는 검증을 하지 않는다
          (type === "upload" || type === "create_upload" || type === "line_update")
        ) {
          // let validated = await approvalDocument.validate(type);
          // 비동기, 동기 둘다 작동하게 변경
          const validated = await Promise.resolve(approvalDocument.validate());

          if (typeof validated === "object") {
            if (!validated.success) {
            }
          }

          // 검증 실패했으면 빠져나간다
          else if (typeof validated === "string") {
            this.customAlert(validated, "알림");
            // alert(validated);
            return;
          }

          if (type === "upload" || type === "create_upload") {
            // 상신
            if (typeof validated !== "object" && !confirm("상신하시겠습니까?")) {
              return;
            }
          }
        }

        // 동국제강 직원 (company_id = 914,915,916,917)이 존재하는지 체크한다. 없으면 false, 있으면 true
        if (type === "upload" || type === "create_upload") {
          const dkCI = [914, 915, 916, 917];
          const myCompanyId = this.$store.state.auth.userInfo.company_id;
          const isMyCompanyDkCI = dkCI.includes(myCompanyId);

          // 동국제강 직원 본인이 상신하는 경우, 결재선 없이도 진행 가능 (백엔드에서 즉시 승인 처리)
          if (!isMyCompanyDkCI) {
            if (approvalLine) {
              const dkCompanyCheck = JSON.parse(approvalLine).reduce((prev, curr) => {
                return prev || dkCI.includes(curr.companyId);
              }, false);

              if (!dkCompanyCheck) {
                this.customAlert("결재선에 동국제강 담당자를 추가해주세요", "알림");
                return;
              }
            }
          }
        }

        const documentCompanyCodes = [];
        try {
          let documentCompanyObj = null;
          if (documentCompany && typeof documentCompany === "string") {
            documentCompanyObj = JSON.parse(documentCompany);
          }
          if (documentCompanyObj && documentCompanyObj.code) {
            documentCompanyCodes.push({
              code: documentCompanyObj.code,
              type: "결재",
            });
          }

          // 수신참조 부서 처리
          let referenceCompaniesArray = [];
          if (referenceCompanies && typeof referenceCompanies === "string") {
            referenceCompaniesArray = JSON.parse(referenceCompanies);
          } else if (Array.isArray(referenceCompanies)) {
            referenceCompaniesArray = referenceCompanies;
          }

          if (referenceCompaniesArray && referenceCompaniesArray.length > 0) {
            for (const company of referenceCompaniesArray) {
              if (company && company.code) {
                documentCompanyCodes.push({
                  code: company.code,
                  type: "참조",
                });
              }
            }
          }
        } catch (e) {
          console.error(e);
        }

        let form = approvalDocument.prevSaveEvent ? await Promise.resolve(approvalDocument.prevSaveEvent(this.form)) : this.form;
        form.header.full = `${form.header.year}-${form.header.month > 9 ? form.header.month : `0${form.header.month}`}-01`;
        form = this.__writeSaveApprovalPostContent(form);

        const projectId = this.projectId;
        const boardSlug = this.boardSlug;
        const title = this.form.body.title;
        const content = JSON.stringify(form);
        let result = null;
        if (type === "create" || type === "re_create") {
          result = await this.$store
            .dispatch("approval/CREATE_POST", {
              isTemp: 1,
              boardSlug,
              parentId: this.parentId,
              projectId,
              title,
              content,
              approvalLine,
              documentCompanyCodes,
              security: "normal",
              priority: "medium",
            })
            .catch((error) => {
              this.customAlert(error.response.data);
              return false;
            });
          if (result === false) return;
          alert("임시저장 되었습니다");
        } else if (type === "update") {
          const postId = this.postId;
          result = await this.$store
            .dispatch("approval/UPDATE_POST", {
              postId,
              unTemp: "n",
              boardSlug,
              projectId,
              title,
              content,
              approvalLine,
              documentCompanyCodes,
              security: "normal",
              priority: "medium",
            })
            .catch((error) => {
              this.customAlert(error.response.data);
              return false;
            });
          if (result === false) return;
          alert("임시저장 되었습니다");
        } else if (type === "upload") {
          const postId = this.postId;
          await this.$store.dispatch("approval/UPDATE_POST", {
            postId,
            unTemp: "y",
            boardSlug,
            projectId,
            title: title || "(제목없음)",
            content,
            approvalLine,
            documentCompanyCodes,
            security: "normal",
            priority: "medium",
          });
          alert("상신 되었습니다");
        } else if (type === "create_upload") {
          result = await this.$store
            .dispatch("approval/CREATE_POST", {
              boardSlug,
              projectId,
              title,
              content,
              approvalLine,
              documentCompanyCodes,
              parentId: this.parentId,
              security: "normal",
              priority: "medium",
            })
            .catch((error) => {
              this.customAlert(error.response.data);
              return false;
            });
          if (result === false) return;
          alert("상신 되었습니다");
        } else if (type === "line_update") {
          // 결재 프로세스에 올라온 사람이 저장 버튼을 눌렀을때
          if (this.board.isTurnApproval) {
            const postId = this.postId;
            const boardId = this.boardId;

            // 업데이트 결재선 정보에, 처리부서 정보를 삽입한다
            const formatApprovalLine = this.board.approvalLine.map((pl) => {
              const obj = { id: pl.user_id, elementType: pl.type_code };
              if (pl.document_company_code) {
                obj.documentCompanyCode = pl.document_company_code;
              }
              return obj;
            });

            return await this.$store.dispatch("board/UPDATE_POST", {
              projectId,
              boardId,
              postId,
              title: title || "(제목없음)",
              content,
              approvalLine: formatApprovalLine,
              documentCompanyCodes,
            });
          }
        }
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        if (approvalDocument.saveAfter) {
          await approvalDocument.saveAfter(result.postId);
        }

        // callback 함수의 리턴값이 false 일 경우, 이후의 동작을 중단한다.
        if (callback && !callback(result)) return;
        // 저장 / 수정 / 삭제 / 상신 등의 이후에 이동할 페이지 타입을 지정합니다.
        else {
          const pageType = this.$refs.approvalDocument.getPostSaveRedirectPageType ? this.$refs.approvalDocument.getPostSaveRedirectPageType() : "list";
          this.moveTabPage(pageType);
        }
      } finally {
        this.loading(false);
      }
    },
    async writeLoadBoardPost(postId) {
      const boardId = this.boardId;
      await this.$store.dispatch("board/LOAD_POST", {
        boardId,
        postId,
      });

      // 내용
      const post = this.$store.state.board.postInfo;
      this.post = post;
      this.writeDocProjectId = post.project_id;

      const normalDocument = this.$refs.normalDocumentComponent;

      let postContent = post.post_content;
      if (normalDocument && normalDocument.prefixSetPostContent) {
        // post 를 읽어온 뒤에 this.form = JSON.parse(post.post_content); 하기전 값에 호출한다.
        // 전달 받은 post.post_content 와 같은 형태로 return 해줘야 한다.
        postContent = await normalDocument.prefixSetPostContent(post);
      }

      if (postContent) {
        let content = JSON.parse(postContent);
        if (this.$route.query["_cp_pid"]) {
          const refDocument = this.$refs.normalDocumentComponent;
          // 복사하기 기능을 사용할 때, 읽어온 데이터 중 일부를 변경하기위해서,
          // Write.vue 의 cpPostContent 함수를 사용한다.
          if (refDocument.cpPostContent) {
            content = refDocument.cpPostContent(content);
          }
        }

        if (!this.writeOriginForm) this.writeOriginForm = JSON.parse(JSON.stringify(content));

        this.form = content;
        if (!this.form.body["fileList"]) {
          this.form.body.fileList = [];
        }
        if (!this.form.body["formList"]) {
          this.form.body.formList = [];
        }

        // post 를 불러온 후에 호출한다.
        if (normalDocument.initForm) {
          normalDocument.initForm(content);
        }
      }

      // NotificationDropDown 컴포넌트의 알림 개수 업데이트
      this.updateNotificationCount();
    },
    async writeSaveBoardPost(type, callback) {
      this.loading(true);

      try {
        let normalDocument = this.$refs["normalDocumentComponent"];
        // 만약 normalDocument 값이 배열일 경우, 배열의 첫번째 값을 가져온다
        if (Array.isArray(normalDocument)) {
          normalDocument = normalDocument[0];
        }

        // 만약 normalDocument.validate 메서드가 존재하면 호출한다
        if (normalDocument.validate) {
          const validated = await Promise.resolve(normalDocument.validate());
          // 검증 실패했으면 빠져나간다
          if (validated) {
            if (validated === "write-custom-alert") return;

            this.customAlert(validated, "알림");
            // alert(validated);
            return;
          }
        }

        if (typeof callback === "function") {
          await callback();
        }

        const form = normalDocument.prevSaveEvent ? await Promise.resolve(normalDocument.prevSaveEvent(this.form)) : this.form;
        form.header.full = `${form.header.year}-${form.header.month > 9 ? form.header.month : `0${form.header.month}`}-01`;

        const projectId = this.projectId;
        const boardId = this.boardId;
        const title = this.form.body.title;
        const content = JSON.stringify(form);

        if (type === "create") {
          const result = await this.$store
            .dispatch("board/CREATE_POST", {
              projectId,
              boardId,
              title,
              content,
            })
            .catch((error) => {
              this.customAlert(error.response.data);
              return false;
            });
          if (result === false) return;
        } else if (type === "update") {
          const postId = this.postId;
          const result = await this.$store
            .dispatch("board/UPDATE_POST", {
              projectId,
              boardId,
              postId,
              title,
              content,
            })
            .catch((error) => {
              this.customAlert(error.response.data);
              return false;
            });
          if (result === false) return;
        }
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        this.moveTabPage("list");
      } finally {
        this.loading(false);
      }
    },

    async writeOnClickApprovalDelete() {
      this.confirmDelete(async () => {
        const boardSlug = this.boardSlug;
        const postId = this.$route.params.postId;
        const result = await this.$store
          .dispatch("approval/DELETE_POST", {
            boardSlug,
            postId,
          })
          .catch((error) => {
            this.customAlert(error.response.data);
            return false;
          });
        if (result === false) return;
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        // 저장 / 수정 / 삭제 / 상싱 등의 이후에 이동할 페이지 타입을 지정합니다.
        const pageType = this.$refs.approvalDocument.getPostSaveRedirectPageType ? this.$refs.approvalDocument.getPostSaveRedirectPageType() : "list";
        this.moveTabPage(pageType);
      });
    },
    /**
     * 결재 승인할 경우 호출되는 함수
     *
     * @param documentCompany 처리부서 정보
     * @param referenceCompanies 참조 부서 정보
     */
    async writeOnClickApprovalOk(documentCompany, referenceCompanies) {
      // 저장
      // dksteel 에서 모든 결재문서는 현재 문서의 결재라인을 우선 저장하고, 승인처리를 한다.
      // 이와 관련하여 moffice 에서 "결재문서 편집 가능" 기능을 on 시켜야 함다.
      const saveForm = await this.writeSaveApprovalPost("line_update", null, documentCompany, referenceCompanies);

      const documentCompanyCodes = [];
      try {
        let documentCompanyObj = null;
        if (documentCompany && typeof documentCompany === "string") {
          documentCompanyObj = JSON.parse(documentCompany);
        }
        if (documentCompanyObj && documentCompanyObj.code) {
          documentCompanyCodes.push({
            code: documentCompanyObj.code,
            type: "결재",
          });
        }
      } catch (e) {
        console.error(e);
      }

      if (saveForm === "Updated") {
        // 승인
        const postId = this.$route.params.postId;
        const result = await this.$store.dispatch("approval/APPROVE_OK", { postId }).catch((error) => {
          this.customAlert(error.response.data);
          return false;
        });
        if (result === false) return;
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        // 저장 / 수정 / 삭제 / 상싱 등의 이후에 이동할 페이지 타입을 지정합니다.
        const pageType = this.$refs.approvalDocument.getPostSaveRedirectPageType ? this.$refs.approvalDocument.getPostSaveRedirectPageType() : "list";
        this.moveTabPage(pageType);
      }
    },
    async writeOnClickApprovalNo(text) {
      // 반려
      const postId = parseInt(this.$route.params.postId);
      const comment = text;
      const result = await this.$store.dispatch("approval/APPROVE_NO", { postId, comment }).catch((error) => {
        this.customAlert(error.response.data);
        return false;
      });
      if (result === false) return;
      this.writeOldForm = JSON.parse(JSON.stringify(this.form));
      // 저장 / 수정 / 삭제 / 상싱 등의 이후에 이동할 페이지 타입을 지정합니다.
      const pageType = this.$refs.approvalDocument.getPostSaveRedirectPageType ? this.$refs.approvalDocument.getPostSaveRedirectPageType() : "list";
      this.moveTabPage(pageType);
    },
    writeOnClickApprovalSave(approvalLine) {
      const parentId = this.$route.query["_p_pid"];
      if (parentId) {
        this.parentId = parentId;
      }
      this.writeSaveApprovalPost(this.postId ? "update" : "create", approvalLine, null, null);
    },
    writeOnClickApprovalRevision(approvalLine, message) {
      if (confirm(message || "개정 할 경우\n기존 문서는 유지되고, 내용이 복사되어 임시저장 됩니다.\n개정 하시겠습니까?")) {
        this.parentId = this.tab.parentId || this.postId;
        this.writeSaveApprovalPost("create", approvalLine, null, null);
      }
    },
    writeOnClickApprovalUpload(approvalLine, documentCompany = null, referenceCompanies = null) {
      const parentId = this.$route.query["_p_pid"];
      if (parentId) {
        this.parentId = parentId;
      }
      if (this.postId) {
        this.writeSaveApprovalPost("upload", approvalLine, documentCompany, referenceCompanies);
      } else {
        this.writeSaveApprovalPost("create_upload", approvalLine, documentCompany, referenceCompanies);
      }
    },
    writeOnClickApprovalRecall(approvalLine) {
      // 결재문서 회수
      if (confirm("회수 후 임시저장 상태로 변경하시겠습니까?")) {
        this.writeSaveApprovalPost("update", approvalLine, null, null);
      }
    },
    writeOnClickApprovalClose() {
      // 닫기
      this.$router.back();
    },
    async writeOnClickNormalRemove() {
      // 게시글을 삭제하시겠습니까?
      this.confirmDelete(async () => {
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        const boardId = this.boardId;
        const postId = this.postId;
        const result = await this.$store.dispatch("board/DELETE_POST", { boardId, postId }).catch((error) => {
          this.customAlert(error.response.data);
          return false;
        });
        if (result === false) return;
        this.writeOldForm = JSON.parse(JSON.stringify(this.form));
        this.moveTabPage("list");
      });
    },
    writeOnClickNormalSave(callback) {
      const parentId = this.$route.query["_p_pid"];
      if (parentId) {
        this.parentId = parentId;
      }

      if (this.postId) {
        this.writeSaveBoardPost("update", callback);
      } else {
        this.writeSaveBoardPost("create", callback);
      }
    },
    async copyOnClickNormal(boardId, postId) {
      // 페이지 이동시 board, post 정보를 전달
      // 신규 작성페이지에서 해당부분 체크하여 대상 content 를 불러오게끔 처리해야함
      this.moveTabPage("write", {
        _cp_pid: postId,
      });
    },
    writeOnClickPrevPost() {
      this.popup.loadPrevBoard = true;
    },
    async writeOnClickExcelDownload() {
      const documentComponent = this.$refs["normalDocumentComponent"];
      if (documentComponent.excelDownload) {
        const info = documentComponent.excelDownload();
        // 엑셀 파일 가져오기
        const response = await fetch(info.resource);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        // 첫번째 시트의 이름 가져오기
        const worksheet = workbook.worksheets[0];

        // 시트에 데이터 입력하기
        if (info.applyDataToSheet) {
          info.applyDataToSheet(worksheet);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const fileName = info.fileName;

        // a 태그 생성 후 자동 클릭
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    },

    writeSaveButtonControl(value) {
      if (this.$refs["refNormalDocument"] && this.$refs["refNormalDocument"].saveButtonControl) {
        this.$refs["refNormalDocument"].saveButtonControl(value);
      }
    },
    writeRemoveButtonControl(value) {
      if (this.$refs["refNormalDocument"] && this.$refs["refNormalDocument"].removeButtonControl) {
        this.$refs["refNormalDocument"].removeButtonControl(value);
      }
    },
    writeSetPrevBoardOptions(value) {
      const boardId = value.boardId;
      const btnLabel = value.btnLabel;
      const prevBoardSelectRow = value.prevBoardSelectRow;
      this.writePrevBoardOptions.boardId = boardId;
      this.writePrevBoardOptions.title = btnLabel;
      this.writePrevBoardOptions.prevBoardSelectRow = prevBoardSelectRow;
    },
    writeOnClickReTempsave(approvalLine, message) {
      if (confirm(message || "재상신을 할 경우,\n기존 문서는 유지 되고, 내용이 복사되어 임시저장됩니다.\n재상신 하시겠습니까?")) {
        this.form.body.stdInfo.department = this.myInfo.department; //소속
        this.form.body.stdInfo.section = this.myInfo.section; //부서
        this.form.body.stdInfo.rank = this.myInfo.rank; //직급
        this.form.body.stdInfo.writer_name = this.myInfo.name; //작성자
        this.form.body.stdInfo.date = new Date().format("yyyy-MM-dd"); //작성일
        this.form.body.stdInfo.rejectedPostId = this.postId; // (재상신을 하게 된)반려된 게시글 ID
        this.parentId = this.tab.parentId; // 부모글 ID
        this.writeSaveApprovalPost("re_create", JSON.stringify([]), null, null, (result) => {
          const postId = result.postId;
          const route = this.$route;
          route.params.postId = postId;
          const routeData = this.$router.resolve(route); // 또는 path 사용 가능
          window.open(routeData.href, "_blank");
          return false;
        });
      }
    },
    writeOnClickCopysave(approvalLine, callback) {
      this.form.body.stdInfo.department = this.myInfo.department; //소속
      this.form.body.stdInfo.section = this.myInfo.section; //부서
      this.form.body.stdInfo.rank = this.myInfo.rank; //직급
      this.form.body.stdInfo.writer_name = this.myInfo.name; //작성자
      this.form.body.stdInfo.date = new Date().format("yyyy-MM-dd"); //작성일
      this.parentId = 0; // 부모글 ID

      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      const date = new Date().format("yyyy-MM-dd");
      const half = month <= 6 ? "상반기" : "하반기";
      const quarter = month <= 3 ? "1" : month <= 6 ? "2" : month <= 9 ? "3" : "4";
      this.form.header = {
        year,
        month,
        date,
        half,
        quarter,
        full: date,
      };

      this.writeSaveApprovalPost("create", JSON.stringify([]), null, null, callback); // 2025-09-18 결재선은 비운다
    },
    writeOnClickSaveMultiple(forms) {
      this.loading(true);
      const date = new Date();
      const header = { ...this.header, full: date.format("yyyy-MM-dd") };
      const posts = forms.posts.map((form) => {
        const content = {
          header,
          body: {
            ...form,
          },
        };
        return {
          title: this.pageTitle,
          content: JSON.stringify(content),
          projectId: this.projectId,
          company_member_join: true,
        };
      });
      HttpRequest.put(`/api/v1/board/post/${this.boardId}/multiple`, { posts })
        .then((response) => {
          if (forms.callback) {
            forms.callback(true, response);
          }
          this.loading(false);
          this.customAlert(`${this.pageTitle} 엑셀 업로드가 완료되었습니다`, this.pageTitle);
        })
        .catch(({ response }) => {
          if (forms.callback) {
            forms.callback(false, response);
          }
          this.loading(false);
          this.customAlert("서버에서 오류가 발생했습니다. 관리자에게 문의해주세요");
        });
    },
    // 오른쪽 컴포넌트 표기 여부
    // true - 표기, false - 미표기
    writeShouldShowRightComponent(shouldShow, mode, key) {
      // 작성모드에 따른 표기 여부 : c - 작성, u - 수정
      const __checkMode = (_mode) => {
        if (_mode) {
          return _mode.split("|").indexOf(mode) > -1;
        }
        return true;
      };

      // 권한에 따른 표기 여부 : c - 작성, r - 읽기, u - 수정, d - 삭제
      const __checkPermission = (_permission) => {
        if (_permission) {
          const permissionList = _permission.split("|");
          const crud = ["c", "r", "u", "d"];
          for (const key of crud) {
            if (permissionList.indexOf(key) > -1) {
              if (this.permission[key] === 1) {
                return true;
              }
            }
          }
        }
        return false;
      };

      // 작성자만 표기 여부
      // NormalDocument.vue / ApprovalDocument2.vue 모두 board.isWriter 를 계산해서 넘겨주므로 이 값을 기준으로 판단한다.
      // (board.author 는 NormalDocument.vue 에서만 설정되어, 결재형 문서에서는 항상 undefined 였다.)
      const __checkOnlyWriter = (_onlyWriter) => {
        if (this.postId && _onlyWriter) {
          return !!this.board.isWriter;
        }
        return true;
      };

      // Object 형태로 들어온 경우
      // shouldShow = {
      //   mode: "c|u",               // c: 작성, u: 수정
      //   freePass: 0,               // 0: freePass 없음, 1: 무조건 표기, -1: 무조건 미표기
      //   permission: "c|r|u|d",     // c: 작성, r: 읽기, u: 수정, d: 삭제 권한이 있을 경우 표기
      //   onlyWriter: true,          // 작성자만 표기
      //   custom: true,              // 그외 조건
      // }
      // Array 형태로 들어온 경우
      // shouldShow = ["c|u", 0, "c|r|u|d", true, true]
      const isArray = Array.isArray(shouldShow);

      // custom 설정이 없는 경우, true 로 설정
      if (isArray) {
        if (shouldShow.length <= 4) shouldShow.push(true);
      } else if (shouldShow.custom === undefined) shouldShow.custom = true;

      const _mode = isArray ? shouldShow[0] : shouldShow.mode;
      const _freePass = isArray ? shouldShow[1] : shouldShow.freePass;
      const _permission = isArray ? shouldShow[2] : shouldShow.permission;
      const _onlyWriter = isArray ? shouldShow[3] : shouldShow.onlyWriter;
      const _custom = isArray ? shouldShow[4] : shouldShow.custom;

      if (__checkMode(_mode)) {
        if (_freePass === 1) {
          return true;
        } else if (_freePass === -1) {
          if (key === "save" /*저장버튼*/) {
            this.board.readonly = true;
          }
          return false;
        }

        if (__checkPermission(_permission) && _custom) {
          // 작성자만 표기 여부 일 때,
          if (_onlyWriter) {
            if (__checkOnlyWriter(_onlyWriter)) return true;

            // 만약 상위 업체에서 수정 가능하도록 설정되어있으면 업체 관계를 확인한다.
            if (this.$store.state.board.boardData.editable_by_parent_company) {
              const editableCompanyIds = this.$store.state.board.postInfo.editable_company_ids;
              const myCompanyId = this.$store.state.auth.companyInfo.id;

              // 내가 상위 업체에 속한 경우,
              if (editableCompanyIds && editableCompanyIds.length > 0 && editableCompanyIds.includes(myCompanyId)) {
                return true;
              }
            }

            // 만약 같은 업체에서 수정이 가능하도록 되어 있을 경우에도 확인한다
            if (this.$store.state.board.boardData.editable_by_company_member && this.$store.state.board.postInfo.author) {
              const authorCompanyId = this.$store.state.board.postInfo.author.company_id ? this.$store.state.board.postInfo.author.company_id : 0;
              const myCompanyId = this.$store.state.auth.companyInfo.id;

              const editable = `${authorCompanyId}` === `${myCompanyId}`;
              if (editable) {
                return true;
              }
            }
          }

          // _onlyWriter 가 false 인 경우, 이전 권한이 true 이면 표기한다.
          else {
            return true;
          }
        }
      }

      return false;
    },
    confirmDelete(confirmFunction) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: "게시글 삭제",
          message: "<p style='width:100%; text-align: center; padding: 8px 0;'>게시글을 삭제하시겠습니까?</p>",
          useConfirm: true,
          confirmBtn: "취소",
          cancelBtn: "삭제",
          cancelFunction: confirmFunction,
          size: "small",
          btnPosition: "center",
          confirmBtnStyle: {
            type: "filled",
            color: "gray",
          },
          cancelBtnStyle: {
            type: "filled",
            color: "danger",
          },
        },
      });
      window.dispatchEvent(customAlert);
    },

    // TODO : ======================== popup ======================== //
    prevBoardSelectRow(value) {
      const func = this.writePrevBoardOptions.prevBoardSelectRow;
      if (func) {
        func(value);
      } else {
        this.popupUpdatePrevForm(value);
      }
    },
    popupUpdatePrevForm(data) {
      // 가능하면 이런건 없애야지...
      if (this.boardId === 261) {
        delete data.headerDate;
        delete data.stdInfo;
        delete data.fieldInfo;

        delete data.hideBudgetDay1;
        delete data.hideBudgetDay2;
        delete data.budgetDay1Total;
        delete data.budgetDay2Total;

        const budgetDay1 = data.budgetDay1.map((_day) => {
          return { ..._day, readonly: false, yesterday: 0, total: 0 };
        });
        const budgetDay2 = data.budgetDay2.map((_day) => {
          return { ..._day, readonly: false, yesterday: 0, total: 0 };
        });

        this.form.body = { ...this.form.body, ...data, budgetDay1, budgetDay2 };

        const setBudgetDayOldData = new CustomEvent("setBudgetDayOldData");
        window.dispatchEvent(setBudgetDayOldData);
      } else {
        this.form.body = JSON.parse(data.post_content).body;
      }
    },

    // ==================================== 리스트 엑셀 다운로드 ==================================
    clickSelectDownload() {
      this.popup.multiExcelDownload = true;
    },

    // NotificationDropDown 컴포넌트의 알림 개수 업데이트 함수
    updateNotificationCount() {
      this.$store.commit("approval/RESET_NOTIFICATION_LOADED_AT");

      // 알림 목록 강제 갱신
      this.$store.dispatch("approval/LOAD_NOTIFICATION_UNREAD_LIST", {
        page_unread: 1,
        per_page_unread: 5,
        days_before_unread: 30,
        isForce: true,
      });

      // 전역 이벤트 발행으로 MyPageAlert 컴포넌트에도 알림
      this.$root.$emit("notifications-updated");
    },
  },
};
</script>

<style scoped lang="scss">
.custom-icon-size {
  padding: 0.679rem 0.679rem !important;
  ::v-deep(.vs-icon) {
    font-size: 20px !important;
  }
}
</style>
